import { NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { UserRole, AGENCY_INFO } from '@/types/rbac.types';
import { cn } from '@/lib/utils';
import smallLogo from '@/assets/auto_symbol_logo.png';
import largeLogo from '@/assets/auto_logo.png';
import {
    LayoutDashboard,
    ScanSearch,
    Car,
    Siren,
    FileSearch,
    Settings,
    LogOut,
    PanelLeftClose,
    PanelLeft,
    X,
    type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface NavItem {
    label: string;
    href: string;
    icon: LucideIcon;
    allowedRoles?: UserRole[];
}

interface NavGroup {
    label: string;
    items: NavItem[];
}

const navGroups: NavGroup[] = [
    {
        label: 'Workspace',
        items: [
            { label: 'Overview', href: '/dashboard', icon: LayoutDashboard },
            { label: 'VIN Lookup', href: '/vin-lookup', icon: ScanSearch, allowedRoles: [UserRole.DVLA, UserRole.CUSTOMS, UserRole.EOCO, UserRole.POLICE] },
            { label: 'Vehicle Registry', href: '/vehicle-history', icon: Car },
        ],
    },
    {
        label: 'Enforcement',
        items: [
            { label: 'Stolen Vehicles', href: '/police/stolen', icon: Siren, allowedRoles: [UserRole.POLICE, UserRole.DVLA] },
            { label: 'Fraud Cases', href: '/eoco/cases', icon: FileSearch, allowedRoles: [UserRole.EOCO] },
        ],
    },
    {
        label: 'Account',
        items: [{ label: 'Settings', href: '/profile', icon: Settings }],
    },
];

const COLLAPSE_KEY = 'fw.sidebar.collapsed';

const readCollapsed = () => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
};

interface SidebarProps {
    isMobileOpen?: boolean;
    onMobileClose?: () => void;
    variant?: 'desktop' | 'mobile';
}

export const Sidebar = ({ isMobileOpen = false, onMobileClose, variant = 'mobile' }: SidebarProps) => {
    const { hasRole, logout, user } = useAuthStore();
    const [isCollapsed, setIsCollapsed] = useState(readCollapsed);
    const location = useLocation();
    const collapsed = variant === 'desktop' && isCollapsed;
    const agency = AGENCY_INFO[user?.role ?? UserRole.DVLA];

    const toggleCollapsed = () => {
        setIsCollapsed(prev => {
            try { localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1'); } catch { /* storage unavailable */ }
            return !prev;
        });
    };

    useEffect(() => {
        if (variant !== 'mobile' || !isMobileOpen) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onMobileClose?.();
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [variant, isMobileOpen, onMobileClose]);

    const groups = navGroups
        .map(g => ({ ...g, items: g.items.filter(i => !i.allowedRoles || hasRole(i.allowedRoles)) }))
        .filter(g => g.items.length > 0);

    const closeIfMobile = () => { if (variant === 'mobile') onMobileClose?.(); };

    return (
        <aside
            aria-label="Main navigation"
            className={cn(
                'flex h-screen flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width,transform] duration-300 ease-standard',
                variant === 'desktop'
                    ? cn('relative z-10', collapsed ? 'w-[4.5rem]' : 'w-64')
                    : cn('fixed inset-y-0 left-0 z-40 w-72 shadow-overlay', isMobileOpen ? 'translate-x-0' : '-translate-x-full')
            )}
        >
            {/* Brand */}
            <div className={cn('flex h-16 shrink-0 items-center border-b border-sidebar-border', collapsed ? 'justify-center px-2' : 'justify-between px-5')}>
                <NavLink to="/dashboard" onClick={closeIfMobile} className="flex items-center rounded-md focus-visible:outline-none focus-visible:shadow-focus" aria-label="FraudWall Auto home">
                    <img
                        src={collapsed ? smallLogo : largeLogo}
                        alt="FraudWall Auto"
                        className={cn('object-contain', collapsed ? 'h-8 w-9' : 'h-7 w-auto max-w-[10.5rem]')}
                    />
                </NavLink>
                {variant === 'mobile' && (
                    <button onClick={onMobileClose} className="rounded-md p-1.5 text-muted-foreground hover:bg-sidebar-muted hover:text-foreground" aria-label="Close menu">
                        <X className="h-5 w-5" />
                    </button>
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5 scrollbar-none">
                {groups.map(group => (
                    <div key={group.label} className="space-y-1">
                        {collapsed ? (
                            <div className="mx-auto mb-2 h-px w-6 bg-sidebar-border first:hidden" aria-hidden />
                        ) : (
                            <p className="overline-label px-3 pb-1.5">{group.label}</p>
                        )}
                        {group.items.map(item => {
                            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                            return (
                                <NavLink
                                    key={item.href}
                                    to={item.href}
                                    onClick={closeIfMobile}
                                    title={collapsed ? item.label : undefined}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={cn(
                                        'group relative flex h-10 items-center gap-3 rounded-lg px-3 text-body-sm font-medium transition-colors focus-visible:outline-none focus-visible:shadow-focus',
                                        collapsed && 'justify-center px-0',
                                        isActive
                                            ? 'bg-primary-subtle font-semibold text-primary-subtle-foreground'
                                            : 'hover:bg-sidebar-muted hover:text-foreground'
                                    )}
                                >
                                    {isActive && <span className="absolute -left-3 top-2 bottom-2 w-[3px] rounded-r-full bg-sidebar-active" aria-hidden />}
                                    <item.icon className={cn('h-[1.125rem] w-[1.125rem] shrink-0', isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')} />
                                    {!collapsed && <span className="truncate">{item.label}</span>}
                                </NavLink>
                            );
                        })}
                    </div>
                ))}
            </nav>

            {/* Agency + session */}
            <div className="shrink-0 space-y-1 border-t border-sidebar-border p-3">
                {!collapsed && (
                    <div className="mb-2 flex items-center gap-3 rounded-lg bg-sidebar-muted p-2.5">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-card">
                            <img src={agency.logo} alt="" className="h-full w-full object-contain p-0.5" />
                        </div>
                        <div className="min-w-0">
                            <p className="truncate text-body-sm font-semibold text-foreground">{agency.short}</p>
                            <p className="truncate text-caption text-muted-foreground">{agency.name}</p>
                        </div>
                    </div>
                )}
                <button
                    onClick={() => { logout(); closeIfMobile(); }}
                    title={collapsed ? 'Sign out' : undefined}
                    className={cn(
                        'flex h-10 w-full items-center gap-3 rounded-lg px-3 text-body-sm font-medium text-danger transition-colors hover:bg-danger-subtle focus-visible:outline-none focus-visible:shadow-focus',
                        collapsed && 'justify-center px-0'
                    )}
                >
                    <LogOut className="h-[1.125rem] w-[1.125rem] shrink-0" />
                    {!collapsed && <span>Sign out</span>}
                </button>
                {variant === 'desktop' && (
                    <button
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        className={cn(
                            'flex h-10 w-full items-center gap-3 rounded-lg px-3 text-body-sm font-medium text-muted-foreground transition-colors hover:bg-sidebar-muted hover:text-foreground focus-visible:outline-none focus-visible:shadow-focus',
                            collapsed && 'justify-center px-0'
                        )}
                    >
                        {collapsed ? <PanelLeft className="h-[1.125rem] w-[1.125rem]" /> : <PanelLeftClose className="h-[1.125rem] w-[1.125rem]" />}
                        {!collapsed && <span>Collapse</span>}
                    </button>
                )}
            </div>
        </aside>
    );
};
